import React from 'react'

import { Box, Stack } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

import Button from '../Button/Button'

export interface FilterSheetFooterProps {
  /**
   * How many filters are STAGED. Drives the Apply label — `Apply (3)` — and
   * nothing else. It deliberately does NOT gate the button; see "Apply was not
   * disabled at zero" below.
   *
   * NO CURRENT CALLER. It only ever labelled the Apply button, and every surface
   * in both apps now auto-applies, so there is no button left to label. Where an
   * applied-filter count still belongs — a toolbar badge — it reads the APPLIED
   * filters rather than a staged set.
   */
  count?: number
  /**
   * Gate the Apply button. There is NO default rule: only the caller can compare
   * what is staged against what is already applied, so only the caller can say
   * "pressing this would change nothing".
   *
   * NO CURRENT CALLER. The Display sheet was the case this existed for
   * (`disabled={!dirty}`) and it no longer stages anything.
   */
  disabled?: boolean
  /**
   * Clear every field in the sheet. Under auto-apply this COMMITS — see the note
   * on semantics — but it must never CLOSE the sheet.
   */
  onReset: () => void
  /**
   * Commit the sheet's current values.
   *
   * OMIT IT to get the AUTO-APPLY footer — reset only, no Apply button. That is
   * how EVERY caller in both apps renders it today; passing it would reintroduce a
   * commit model the two apps have converged away from. See the note below before
   * doing so.
   */
  onApply?: () => void
  /**
   * The reset button's label. Defaults to `Reset`.
   *
   * `Clear` on the platform drawer, where there is no Apply for `Reset` to pair
   * against as one verb versus another — the word is doing a different job when
   * it is the footer's only control.
   */
  resetLabel?: string
  /**
   * `sheet` — a phone bottom sheet. Two EXACTLY equal-width buttons at 44px.
   * `panel` — a pointer-driven side panel. Right-aligned, intrinsic width, 40px.
   *
   * The split is by input device, not by app. 44 is a thumb target and 50/50 is
   * what keeps "Apply (12)" from widening its half on a 375px sheet; a side panel
   * is clicked with a pointer and its controls sit at the standard 40px control
   * height, right-aligned like every other panel footer in either app.
   */
  mode?: 'sheet' | 'panel'
  /**
   * Stretch the buttons across the footer even in `panel` mode.
   *
   * `panel` right-aligns at `minWidth: 120`, which is correct for platform's 500px
   * filter drawer: a pair of controls sitting at the end of a wide row. It is
   * wrong in a NARROW container — the app's Layout popover is ~296px, and a lone
   * right-aligned Reset there has nothing to align against, so the empty left half
   * reads as a missing button rather than as alignment.
   *
   * Opt-in rather than derived from `onApply`, deliberately: platform's Clear is
   * ALSO a lone reset-only panel button, and it is right-aligned on purpose. The
   * axis that actually decides this is the container's WIDTH, which a component
   * cannot see, so the caller states it.
   */
  fullWidthReset?: boolean
  /**
   * Overrides the primary button's text. Defaults to `Apply` / `Apply (n)`.
   *
   * For a footer whose primary is not a COMMIT. The sort sheet auto-applies, so
   * it has nothing to apply — but it does have an "Add" that belongs in the
   * primary slot, paired with Reset on one row like every other drawer's footer.
   * Without this the pair had to be hand-rolled beside the shared footer, which
   * is how the sort sheet ended up with a full-width Add stacked ABOVE a
   * full-width Reset instead of the left/right pair the others use.
   */
  applyLabel?: React.ReactNode
  /** Merged over the wrapper. Positioning and gutters are the CALLER's — see below. */
  sx?: SxProps<Theme>
}

/**
 * The footer every filter sheet and filter panel uses, in BOTH apps.
 *
 * Layer 1 (`@repo/ui`) as of VERA-315: it began as an `apps/app`-only component
 * (four sheets had four footers) and was promoted here so the platform's filter
 * drawer renders the same code. The user's ask was parity — *"reuse the platform
 * component so they are always in sync"* — and parity that depends on someone
 * remembering to mirror an edit is not parity.
 *
 * ## What is shared and what is not
 *
 * SHARED: the buttons themselves, their order, their sizing rules, and the two
 * semantic rules below (Reset does not apply; Apply is not gated at zero). Those
 * are the things that actually drifted.
 *
 * NOT SHARED: where the footer sits and how far it is inset. Positioning is
 * caller-owned via `sx`, because each panel legitimately matches its OWN header:
 * the platform drawer's header pays 24px, `apps/app`'s `DrawerPopup` header pays
 * a hard-coded 16px. Baking either number in here would put one app's field
 * labels and its panel title on two different left edges.
 *
 * ## ONE commit model now: auto-apply. `onApply` has no live caller.
 *
 * This section used to document TWO models — the platform drawer auto-applying on
 * a 400ms debounce, `apps/app`'s sheets staging behind an explicit Apply — and
 * argued the split was principled: "forcing the app onto auto-apply would remove a
 * commit point its sheets are built around (a half-typed date range would fire a
 * query)."
 *
 * That argument was answered rather than overruled. `apps/app` moved to auto-apply
 * at both breakpoints (VERA-315, user directive), on the same 400ms debounce, and
 * the date-range hazard is handled where the invalid state is actually defined: a
 * range with a `from` and no `to` stages without scheduling a commit, so the list
 * holds still until the range is complete (`CertificatesFilter`'s `stageDate`).
 * The hazard was real; it just did not need a button to solve it.
 *
 * So EVERY caller in both apps now omits `onApply` and gets the reset-only footer.
 * `onApply`, `count` and `disabled` are retained — the prop contract is still
 * coherent and a future surface may genuinely want a commit point — but nothing
 * exercises them today. Treat them as unproven if you reach for one, and read the
 * history below before reintroducing the pattern they belong to.
 *
 * ## Reset COMMITS under auto-apply — a deliberate reversal
 *
 * This component was extracted partly to enforce the opposite rule. Scans,
 * Provenance and Activity each short-circuited "Clear all" into an immediate
 * `onApply`, so it closed the sheet and re-ran the query; the certificates sheet
 * cleared the fields and left you in it, and that was judged correct: "a sheet
 * with an explicit Apply has already told you nothing commits until you press it,
 * and a Reset that silently commits breaks that promise."
 *
 * The promise went with the button. With no Apply, refusing to commit a clear
 * would leave a sheet that cannot reach the empty state at all — the exact dead
 * end described two paragraphs down, arrived at from the other direction. So Reset
 * now commits, on the same debounce as every other control, and the half of the
 * rule that survives is the half that was never about Apply: **Reset does not
 * CLOSE the sheet.** You clear a filter in order to pick a different one.
 *
 * ## Apply was NOT disabled at zero (history, for the same reason)
 *
 * It was, on the argument that "with nothing set, Apply and Reset do the same
 * nothing". That holds only while nothing is APPLIED, and it broke the one
 * sequence Reset exists for: `count` measures the STAGED set, so once a filter is
 * committed a staged zero is not "nothing to do" — it is the value "clear it".
 * Disabling Apply there made the cleared state impossible to commit. Reproduced on
 * the certificates sheet: apply Status = Lost, reopen, press Reset (pills clear,
 * Apply greys out), press Apply, and the list is still filtered to Lost.
 *
 * `SortDrawer`'s commit is the independent witness: its else-branch is commented
 * "the sheet was emptied, so clearing IS the commit" and was unreachable, because
 * the shared footer had greyed out the only button that could reach it. Under
 * auto-apply that branch is finally live, reached by Reset itself.
 *
 * Kept here because the reasoning is what `disabled` is FOR, and the next person
 * to pass it should know which mistake it is easy to make with it.
 *
 * ## Every dimension here is an ABSOLUTE px, and that is mandatory
 *
 * working-conventions §47. A layer-1 component renders under whichever app's
 * theme mounts it, and the two apps have DIFFERENT spacing bases — `@repo/ui`
 * and `apps/platform` are 4px, `apps/app` never overrides MUI's default 8px. So
 * `theme.spacing(n)` inside this file would render at double size in `apps/app`,
 * and unlike a per-call-site `spacing={n}` there is no "convert per app"
 * available from in here. The values below (`24`, `16`, `12`, `44`, `40`) are
 * the pixel values both apps were already rendering before this was shared.
 *
 * ## The ONE pixel that changed on adoption, and why it is the right one
 *
 * `panel` mode draws a **12px** button radius. `apps/app`'s desktop filter panel
 * already used 12; the platform drawer used `borderRadius: 2` — 8px on its 4px
 * base — inside a panel whose own corner is `borderRadius: 3`, i.e. 12. So the
 * platform's footer button disagreed with the panel containing it by 4px, and the
 * two apps disagreed with each other. Converging on 12 fixes both at once, and a
 * button radius quietly diverging from its own container is exactly the class of
 * drift this component was promoted to layer 1 to stop. Everything else — heights,
 * gaps, alignment, labels, padding — renders identically to what each app shipped.
 */
export const FilterSheetFooter = ({
  count,
  disabled,
  onReset,
  onApply,
  applyLabel,
  resetLabel = 'Reset',
  mode = 'sheet',
  fullWidthReset = false,
  sx,
}: FilterSheetFooterProps) => {
  const panel = mode === 'panel'
  /* Stretched panel: panel CHROME (40px, 12px radius) with sheet ALIGNMENT. */
  const stretched = panel && fullWidthReset
  const controlHeight = panel ? 40 : 44

  return (
    /*
     * `mt: 'auto'` and NOTHING else by default — no padding, no divider, no
     * background. That is the certificates template verbatim, and the first
     * version of this component got it wrong: it kept the Scans drawer's `p: 2` +
     * `borderTop`, which is what the template deliberately does not have. The
     * result was a rule across the sheet and a band of dead space under the
     * buttons, on a sheet whose whole point was to match.
     *
     * The sheet already pads its own content; a footer that pads again is padding
     * twice. And the divider draws a line between the fields and the two controls
     * that act on them, which is the one place the sheet does not need dividing.
     *
     * `flexShrink: 0` is what PINS it. The sheets are flex columns whose body
     * scrolls; without this the footer is a shrinkable flex item and a long
     * filter list squeezes it instead of scrolling past it.
     */
    <Box
      sx={{
        /*
         * `mt: 'auto'` pushes the footer to the bottom when the sheet is taller
         * than its content; `paddingTop` guarantees the gap when it is not. Both
         * are needed — `auto` alone collapses to ZERO on a content-height sheet,
         * which is why the gap above these buttons varied per drawer depending on
         * how many filters that drawer happened to have.
         */
        marginTop: 'auto',
        paddingTop: '24px',
        flexShrink: 0,
        ...(sx as object),
      }}
    >
      {/* Row, not column. Stacked full-width buttons cost a whole extra row of a
          sheet already competing with the keyboard on a phone. */}
      <Stack
        direction='row'
        sx={{
          gap: panel ? '12px' : '16px',
          justifyContent: panel && !stretched ? 'flex-end' : 'stretch',
        }}
      >
        <Button
          variant='secondary'
          fullWidth={!panel || stretched}
          onClick={onReset}
          sx={
            panel
              ? {
                  height: controlHeight,
                  borderRadius: '12px',
                  textTransform: 'capitalize',
                  ...(stretched ? { flex: '1 1 0' } : { minWidth: 120 }),
                }
              : { flex: '1 1 0', height: controlHeight }
          }
        >
          {resetLabel}
        </Button>
        {onApply && (
          <Button
            variant='primary'
            fullWidth={!panel || stretched}
            disabled={disabled ?? false}
            onClick={onApply}
            sx={
              panel
                ? {
                    height: controlHeight,
                    borderRadius: '12px',
                    ...(stretched ? { flex: '1 1 0' } : { minWidth: 120 }),
                  }
                : { flex: '1 1 0', height: controlHeight }
            }
          >
            {applyLabel ?? (count && count > 0 ? `Apply (${count})` : 'Apply')}
          </Button>
        )}
      </Stack>
    </Box>
  )
}

export default FilterSheetFooter
